import {
  DevHubBox,
  DevHubTypography,
  DevHubButton,
  DevHubDialog,
  DevHubDialogTitle,
  DevHubDialogContent,
  DevHubDialogActions,
} from '@devxdiscover/devhub-ui';
import { ChangesSummaryItem } from '../types';

interface DataLeadChangesReviewDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  changes: ChangesSummaryItem[];
  viewMode: 'per-app' | 'per-lead';
  onViewModeChange: (mode: 'per-app' | 'per-lead') => void;
}

/**
 * Dialog for reviewing data lead assignment changes before submission.
 * Shows changes either grouped by application or by data lead.
 * @param root0 - Props object containing dialog configuration
 * @param root0.open - Whether the dialog is open
 * @param root0.onClose - Callback when dialog is closed
 * @param root0.onConfirm - Callback when changes are confirmed
 * @param root0.changes - Array of changes to display
 * @param root0.viewMode - Current view mode ('per-app' or 'per-lead')
 * @param root0.onViewModeChange - Callback to change view mode
 */
const DataLeadChangesReviewDialog = ({
  open,
  onClose,
  onConfirm,
  changes,
  viewMode,
  onViewModeChange,
}: DataLeadChangesReviewDialogProps): JSX.Element => {
  // Helper function to get changes grouped by data lead
  const getChangesPerDataLead = () => {
    const leadChanges: Record<
      string,
      {
        leadName: string;
        applications: Array<{
          applicationId: number;
          applicationName: string;
          action: 'added' | 'removed';
        }>;
      }
    > = {};

    changes.forEach(appChange => {
      appChange.changes.forEach(change => {
        if (!leadChanges[change.leadLogin]) {
          leadChanges[change.leadLogin] = {
            leadName: change.leadName,
            applications: [],
          };
        }
        leadChanges[change.leadLogin].applications.push({
          applicationId: appChange.applicationId,
          applicationName: appChange.applicationName,
          action: change.action,
        });
      });
    });

    return leadChanges;
  };

  return (
    <DevHubDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DevHubDialogTitle>Review Changes</DevHubDialogTitle>

      <DevHubDialogContent>
        <DevHubBox style={{ marginBottom: '1rem' }}>
          <DevHubTypography variant="body1" style={{ marginBottom: '1rem' }}>
            Please review the following changes before submitting:
          </DevHubTypography>

          {/* View Mode Toggle */}
          <DevHubBox
            style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}
          >
            <DevHubButton
              variant={viewMode === 'per-app' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => onViewModeChange('per-app')}
            >
              By Application
            </DevHubButton>
            <DevHubButton
              variant={viewMode === 'per-lead' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => onViewModeChange('per-lead')}
            >
              By Data Lead
            </DevHubButton>
          </DevHubBox>

          {/* Changes Display */}
          <DevHubBox
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              padding: '1rem',
            }}
          >
            {viewMode === 'per-app' ? (
              // Per Application View
              <DevHubBox>
                {changes.map(appChange => (
                  <DevHubBox
                    key={appChange.applicationId}
                    style={{
                      marginBottom: '1rem',
                      paddingBottom: '1rem',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <DevHubTypography
                      variant="subtitle2"
                      style={{
                        fontWeight: 'bold',
                        marginBottom: '0.5rem',
                      }}
                    >
                      {appChange.applicationName}
                    </DevHubTypography>
                    {appChange.changes.map((change, index) => (
                      <DevHubBox
                        key={index}
                        style={{
                          marginLeft: '1rem',
                          marginBottom: '0.25rem',
                        }}
                      >
                        <DevHubTypography
                          variant="body2"
                          style={{
                            color:
                              change.action === 'added' ? '#2e7d32' : '#d32f2f',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                          }}
                        >
                          <span style={{ fontWeight: 'bold' }}>
                            {change.action === 'added'
                              ? '+ Added:'
                              : '- Removed:'}
                          </span>
                          {change.leadName} ({change.leadLogin})
                        </DevHubTypography>
                      </DevHubBox>
                    ))}
                  </DevHubBox>
                ))}
              </DevHubBox>
            ) : (
              // Per Data Lead View
              <DevHubBox>
                {Object.entries(getChangesPerDataLead()).map(
                  ([leadLogin, leadData]) => (
                    <DevHubBox
                      key={leadLogin}
                      style={{
                        marginBottom: '1rem',
                        paddingBottom: '1rem',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <DevHubTypography
                        variant="subtitle2"
                        style={{
                          fontWeight: 'bold',
                          marginBottom: '0.5rem',
                        }}
                      >
                        {leadData.leadName} ({leadLogin})
                      </DevHubTypography>
                      {leadData.applications.map((app, index) => (
                        <DevHubBox
                          key={index}
                          style={{
                            marginLeft: '1rem',
                            marginBottom: '0.25rem',
                          }}
                        >
                          <DevHubTypography
                            variant="body2"
                            style={{
                              color:
                                app.action === 'added' ? '#2e7d32' : '#d32f2f',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                            }}
                          >
                            <span style={{ fontWeight: 'bold' }}>
                              {app.action === 'added'
                                ? '+ Added to:'
                                : '- Removed from:'}
                            </span>
                            {app.applicationName}
                          </DevHubTypography>
                        </DevHubBox>
                      ))}
                    </DevHubBox>
                  ),
                )}
              </DevHubBox>
            )}

            {changes.length === 0 && (
              <DevHubTypography
                variant="body2"
                style={{
                  color: '#666',
                  textAlign: 'center',
                  padding: '2rem',
                }}
              >
                No changes to display
              </DevHubTypography>
            )}
          </DevHubBox>
        </DevHubBox>
      </DevHubDialogContent>

      <DevHubDialogActions>
        <DevHubButton onClick={onClose} variant="outlined">
          Cancel
        </DevHubButton>
        <DevHubButton
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={changes.length === 0}
        >
          Submit Changes
        </DevHubButton>
      </DevHubDialogActions>
    </DevHubDialog>
  );
};

export default DataLeadChangesReviewDialog;
